// Debug3d.tsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Group, Title, Text, Divider } from "@mantine/core";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useAppDispatch, useAppSelector } from "../../app/hooks"; // Antag att du har detta
import { selectDebugThreeDItems, IDebugGeometry, appendThreeDItemsInRedux } from "../DebugSlice";

type Props = {};

export const ThreeDViewer: React.FC<Props> = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<string>("Släpp en .json-fil här (från debug-geometri)");
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);

  const dispatch = useAppDispatch();
  const threeDItems = useAppSelector(selectDebugThreeDItems);

  // Initiera Three.js scen
  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff); // Vit för kontrast
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1e12
    ); // Ännu större far-plane
    camera.position.set(0, 1000, 1000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true });
    renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25; // Högre värde för mjukare, mindre skak
    controlsRef.current = controls;

    // Ljus
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(10000, 10000, 10000);
    scene.add(directionalLight);

    // Axlar för referens
    const axesHelper = new THREE.AxesHelper(1000);
    scene.add(axesHelper);

    // Animationsloop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const handleResize = () => {
      if (canvasRef.current && cameraRef.current && rendererRef.current) {
        const { clientWidth, clientHeight } = canvasRef.current;
        camera.aspect = clientWidth / clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(clientWidth, clientHeight);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      renderer.dispose();
    };
  }, []);

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.endsWith(".json")) {
      setStatus("Ogiltig fil – måste vara .json från debug-geometri");
      return;
    }

    setStatus(`Läser in ${file.name}...`);

    const text = await file.text();
    const json: IDebugGeometry = JSON.parse(text);

    // Dispatch till Redux (append för att lägga till flera filer)
    dispatch(appendThreeDItemsInRedux([json]));

    setStatus(`JSON inläst och lagrad i Redux – renderar geometrier...`);
  }

  // Rendera från Redux-data när threeDItems ändras
  useEffect(() => {
    if (threeDItems.length === 0) return;

    // Rensa scenen från tidigare meshes (behåll ljus, axlar etc)
    if (sceneRef.current) {
      sceneRef.current.children = sceneRef.current.children.filter(
        (child) =>
          !(child instanceof THREE.Mesh) ||
          child.name === "axes" || // Behåll axlar om namngiven
          child.type === "DirectionalLight" ||
          child.type === "AmbientLight"
      );
    }

    threeDItems.forEach((debugData) => {
      renderItem(debugData);
    });

    // Auto-fit efter alla items renderats
    autoFitCamera();

    setStatus(
      `Rendering klar (${threeDItems.length} item${
        threeDItems.length > 1 ? "s" : ""
      }) – navigera med musen`
    );
  }, [threeDItems]);

  function renderItem(debugData: IDebugGeometry) {
    if (!sceneRef.current) return;

    const geometries = debugData.geometries;

    geometries.forEach((geom) => {
      const vertexData = new Float32Array(geom.vertexData);
      const indexData = new Uint32Array(geom.indexData);

      const threeGeometry = new THREE.BufferGeometry();
      const positions = new Float32Array(vertexData.length / 2);
      const normals = new Float32Array(vertexData.length / 2);
      for (let k = 0; k < vertexData.length; k += 6) {
        positions[k / 2] = vertexData[k];
        positions[k / 2 + 1] = vertexData[k + 1];
        positions[k / 2 + 2] = vertexData[k + 2];
        normals[k / 2] = vertexData[k + 3];
        normals[k / 2 + 1] = vertexData[k + 4];
        normals[k / 2 + 2] = vertexData[k + 5];
      }
      threeGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      threeGeometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
      threeGeometry.setIndex(Array.from(indexData));

      // Applicera matrix
      const matrix = new THREE.Matrix4().fromArray(geom.transformationMatrix);
      threeGeometry.applyMatrix4(matrix);

      // Beräkna bounds
      threeGeometry.computeBoundingBox();
      console.log("Geometry min:", threeGeometry.boundingBox?.min);
      console.log("Geometry max:", threeGeometry.boundingBox?.max);

      // Material
      const material = new THREE.MeshStandardMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        wireframe: false,
      });

      const mesh = new THREE.Mesh(threeGeometry, material);
      sceneRef.current?.add(mesh);
    });
  }

  function autoFitCamera() {
    if (!sceneRef.current || !cameraRef.current || !controlsRef.current) return;

    const box = new THREE.Box3();
    sceneRef.current.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        box.expandByObject(obj);
      }
    });

    console.log("Overall min:", box.min);
    console.log("Overall max:", box.max);

    if (!box.isEmpty()) {
      const center = new THREE.Vector3();
      box.getCenter(center);
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = cameraRef.current.fov * (Math.PI / 180);
      let cameraDist = maxDim / Math.tan(fov / 2);
      cameraDist *= 1.5;

      cameraRef.current.position.set(
        center.x + cameraDist / 2,
        center.y + cameraDist,
        center.z + cameraDist / 2
      );
      cameraRef.current.lookAt(center);
      controlsRef.current.target.copy(center);
      controlsRef.current.update();

      console.log("Camera positioned at:", cameraRef.current.position);
    }
  }

  return (
    <Box h="100%">
      <Flex direction="column" gap="sm" h="100%">
        <Group justify="space-between">
          <Title order={3}>3D Debug Viewer (JSON)</Title>
        </Group>

        {/* Dropzone */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          p="md"
          style={{
            border: "2px dashed var(--mantine-color-gray-4)",
            borderRadius: 8,
            textAlign: "center",
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <Text>{status}</Text>
        </Box>

        <Divider my={4} />

        {/* Canvas */}
        <Box style={{ flex: 1, minHeight: 0 }}>
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
        </Box>
      </Flex>
    </Box>
  );
};
